/**
 * useAnalyticsCapabilities Hook
 *
 * Provides analytics capabilities based on user's subscription plan.
 * Maps subscription plans to analytics feature levels and restrictions.
 *
 * Analytics Levels:
 * - "none": Free/Basic plan - minimal analytics, campaign status only
 * - "advanced": Pro plan - full metrics, limited export formats, limited date range
 * - "full": Enterprise plan - all features including AI insights, unlimited history
 *
 * Usage:
 *   const { analyticsLevel, isAvailable, isAdvanced, isFull, canExport, canViewInsights } = useAnalyticsCapabilities();
 *   if (!isAvailable) {
 *     return <UpgradePrompt />;
 *   }
 */

import { useSubscription } from '@/hooks/useSubscription';

export interface AnalyticsCapability {
  analyticsLevel: 'none' | 'advanced' | 'full';
  isAvailable: boolean;
  isAdvanced: boolean;
  isFull: boolean;
  canExport: boolean;
  canViewCharts: boolean;
  canViewInsights: boolean;
  canViewBreakdowns: boolean;
  canAccessRecentActivity: boolean;
  retentionDays: number;
  maxCustomDateRange: number;
  exportFormats: string[];
  supportedPlatforms: string[];
  message: string;
}

/**
 * Map subscription plan to analytics level
 * Aligns with entitlement service tiers
 */
const planToAnalyticsLevel = (plan: string | undefined): 'none' | 'advanced' | 'full' => {
  if (!plan) return 'none';

  const planLower = plan.toLowerCase();

  // Enterprise - full analytics
  if (planLower === 'enterprise') {
    return 'full';
  }

  // Pro/Professional - advanced analytics
  if (planLower === 'pro' || planLower === 'professional' || planLower === 'starter') {
    return 'advanced';
  }

  // Free/Basic/default - no analytics
  return 'none';
};

const DEMO_MODE = import.meta.env.VITE_DEMO_MODE === 'true';

export const useAnalyticsCapabilities = (): AnalyticsCapability => {
  const { subscription, loading } = useSubscription();

  if (DEMO_MODE) return {
    analyticsLevel: 'full',
    isAvailable: true,
    isAdvanced: true,
    isFull: true,
    canExport: true,
    canViewCharts: true,
    canViewInsights: true,
    canViewBreakdowns: true,
    canAccessRecentActivity: true,
    retentionDays: 365,
    maxCustomDateRange: -1,
    exportFormats: ['csv', 'pdf', 'json'],
    supportedPlatforms: ['meta', 'google_ads', 'pinterest'],
    message: '',
  };

  // Return loading state while fetching subscription
  if (loading) {
    return {
      analyticsLevel: 'none',
      isAvailable: false,
      isAdvanced: false,
      isFull: false,
      canExport: false,
      canViewCharts: false,
      canViewInsights: false,
      canViewBreakdowns: false,
      canAccessRecentActivity: false,
      retentionDays: 0,
      maxCustomDateRange: 0,
      exportFormats: [],
      supportedPlatforms: [],
      message: 'Loading subscription data...',
    };
  }

  const plan = subscription?.plan || '';
  const analyticsLevel = planToAnalyticsLevel(plan);

  // The backend derives supported_platforms from which channels the
  // deployment actually has switched on, so prefer it over the static lists
  // below — otherwise the dashboard advertises a platform tile that can never
  // have data behind it.
  const planPlatforms = subscription?.supported_platforms;

  // Define capabilities per level
  const capabilities: Record<'none' | 'advanced' | 'full', AnalyticsCapability> = {
    none: {
      analyticsLevel: 'none',
      isAvailable: false,
      isAdvanced: false,
      isFull: false,
      canExport: false,
      canViewCharts: false,
      canViewInsights: false,
      canViewBreakdowns: false,
      canAccessRecentActivity: true, // Basic alerts always available
      retentionDays: 0,
      maxCustomDateRange: 0,
      exportFormats: [],
      supportedPlatforms: [],
      message: 'Analytics features are available with Pro or Enterprise plan. Upgrade to see detailed metrics, charts, and insights.',
    },
    advanced: {
      analyticsLevel: 'advanced',
      isAvailable: true,
      isAdvanced: true,
      isFull: false,
      canExport: true,
      canViewCharts: true,
      canViewInsights: false, // AI insights only for Enterprise
      canViewBreakdowns: false, // Breakdowns only for Enterprise
      canAccessRecentActivity: true,
      retentionDays: 90,
      maxCustomDateRange: 180,
      exportFormats: ['csv', 'pdf'],
      // Fallback only — the live list comes from the plan (see planPlatforms).
      supportedPlatforms: planPlatforms ?? ['meta', 'google_ads', 'pinterest'],
      message: 'You have access to Pro analytics. For AI-powered insights and advanced breakdowns, upgrade to Enterprise.',
    },
    full: {
      analyticsLevel: 'full',
      isAvailable: true,
      isAdvanced: true,
      isFull: true,
      canExport: true,
      canViewCharts: true,
      canViewInsights: true,
      canViewBreakdowns: true,
      canAccessRecentActivity: true,
      retentionDays: 365,
      maxCustomDateRange: -1, // Unlimited
      exportFormats: ['csv', 'pdf', 'json'],
      supportedPlatforms: planPlatforms ?? ['meta', 'google_ads', 'tiktok', 'pinterest'],
      message: 'You have full Enterprise analytics access.',
    },
  };

  return capabilities[analyticsLevel];
};

export default useAnalyticsCapabilities;
